import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { FormInput } from '@/components/FormInput';
import { useAccount } from '@/hooks/useAccount';
import { amplitudeModule } from '@/modules/amplitude';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { useOnboarding } from '@/pages/Onboarding/context';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { ChatMessage, NameInput } from './components';
import { UniversalNameScreenConfig } from './types';
import {
  PageStyles,
  ChatContainerStyles,
  AnimatedBottomButtonContainer,
} from './UniversalNameScreen.styles';

interface UniversalNameScreenProps {
  config: UniversalNameScreenConfig;
  onNextStep?: () => void;
}

export const UniversalNameScreen: React.FC<UniversalNameScreenProps> = ({
  config,
  onNextStep,
}) => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Name',
      });
    }
  }, []);

  const { wizard } = useOnboarding();
  const { refreshUser } = useAccount();
  const form = useFormContext();
  const [showInitialMessage, setShowInitialMessage] = React.useState(false);
  const [showNameInput, setShowNameInput] = React.useState(false);
  const [userName, setUserName] = React.useState<string>('');
  const [showUserResponse, setShowUserResponse] = React.useState(false);
  const [showFollowUpMessages, setShowFollowUpMessages] = React.useState(false);
  const [showBottomButton, setShowBottomButton] = React.useState(false);

  const inputRef = React.useRef<HTMLIonInputElement>(null);

  const formName = form.watch('name');
  const disableNextStep = !formName;

  // Show initial message
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowInitialMessage(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Show name input after initial message
  React.useEffect(() => {
    if (showInitialMessage) {
      const timer = setTimeout(() => {
        setShowNameInput(true);
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.setFocus();
          }
        }, 100);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [showInitialMessage]);

  const handleNameSubmit = () => {
    if (formName) {
      setUserName(formName);
      setShowUserResponse(true);
      setShowNameInput(false);

      setTimeout(() => {
        setShowFollowUpMessages(true);
      }, 500);
    }
  };

  // Show bottom button after follow-up messages
  React.useEffect(() => {
    if (showFollowUpMessages) {
      const timer = setTimeout(() => {
        setShowBottomButton(true);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [showFollowUpMessages]);

  const handleNextStep = () => {
    userService
      .createOrUpdate({
        name: formName,
      })
      .then(() => {
        refreshUser();
      });

    if (onNextStep) {
      onNextStep();
    } else {
      wizard.nextStep();
    }
  };

  const handleBack = () => {
    if (config.backToStep) {
      wizard.goToStep(config.backToStep);
    } else {
      wizard.previousStep();
    }
  };

  return (
    <PageStyles
      backgroundImage={config.backgroundImage}
      background={config.backgroundTheme || 'dark-solid'}
    >
      <IonContent>
        <OnboardingNavigation onBack={handleBack} />

        <ChatContainerStyles>
          {/* Initial message */}
          <ChatMessage
            content={config.messages.initial.content}
            variant={config.messages.initial.variant}
            show={showInitialMessage}
            delay={0}
          />

          {/* User response message */}
          {userName && config.messages.userResponse && (
            <ChatMessage
              content={userName}
              variant={config.messages.userResponse.variant}
              show={showUserResponse}
              delay={0}
            />
          )}

          {/* Follow-up messages */}
          {config.messages.followUp.map((message, index) => {
            const shouldShow = showFollowUpMessages && userName.length > 0;

            // Replace dynamic content in messages
            let messageContent = message.content;
            if (
              messageContent.includes('${userName}') ||
              messageContent.includes('{userName}')
            ) {
              messageContent = messageContent.replace(
                /\$\{userName\}|\{userName\}/g,
                userName,
              );
            }

            return (
              <ChatMessage
                key={index}
                content={messageContent}
                variant={message.variant}
                show={shouldShow}
                delay={index}
              />
            );
          })}
        </ChatContainerStyles>

        <NameInput show={showNameInput}>
          <FormInput
            form={form}
            hidden={false}
            onSubmit={handleNameSubmit}
            disableButton={disableNextStep}
            ref={inputRef}
            name="name"
            type="text"
            placeholder="Type your name"
            theme="primary"
            maxLength={20}
          />
        </NameInput>

        <AnimatedBottomButtonContainer show={showBottomButton}>
          <OnboardingFooter
            onContinue={handleNextStep}
            buttonRadius={'md'}
            buttonSize={'large'}
            buttonText={config.buttonText}
          />
        </AnimatedBottomButtonContainer>
      </IonContent>
    </PageStyles>
  );
};
